import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Plus, Tag, CheckCircle2, Truck, CreditCard } from "lucide-react";
import api, { formatRp, imageUrl, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import VoucherPicker from "@/components/VoucherPicker";
import PromoPopup from "@/components/PromoPopup";
import AddressForm from "@/components/AddressForm";
import LocationPicker from "@/components/LocationPicker";
import CourierRatesGrouped from "@/components/CourierRatesGrouped";
import { biteshipServiceTypes, filterRatesByServiceType, reconcileServiceTypeFilter } from "@/lib/biteshipServiceTypes";
import { COD_METHOD, codOptionVisible, codQuoteKey, checkoutSummary } from "@/lib/biteshipCod";

export default function CheckoutPage() {
  const { user, loading: authLoading } = useAuth();
  const { items, cartTotal, fetchCart } = useCart();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState({ methods: [], bank_accounts: [], qris_image: null });

  const [addressId, setAddressId] = useState("");
  const [shipping, setShipping] = useState("");
  // Service category Jubelio (optional; admin set default)
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceCategoryId, setServiceCategoryId] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState(""); // Biteship: filter tipe layanan dari rates aktual
  // Rate dari /api/shipping/rates (kalau berhasil). Format: {courier_code, courier_name, service, cost, etd}
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  // ratesError: hanya diisi jika TIDAK ada rate sama sekali (fatal error untuk customer)
  // Kalau ada fallback rates valid → simpan ke ratesInfoLog (internal/dev) saja
  const [ratesError, setRatesError] = useState(null);
  const [ratesDegraded, setRatesDegraded] = useState(false);
  const [categoryUnavailable, setCategoryUnavailable] = useState(false);
  // Provider used dari /shipping/rates response (untuk dikirim ke /checkout)
  const [providerUsed, setProviderUsed] = useState(null);
  // Sprint UX Cleanup: shippingMode dari response /shipping/rates ("realtime" | "flat").
  // Default "realtime" supaya tidak pernah ada path render flat fallback secara legacy.
  const [shippingMode, setShippingMode] = useState("realtime");
  const [selectedRate, setSelectedRate] = useState(null);
  const [payment, setPayment] = useState("");
  const [codQuote, setCodQuote] = useState(null); // hasil /shipping/cod-quote (server-authoritative)
  const [codLoading, setCodLoading] = useState(false);
  const [codError, setCodError] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [notes, setNotes] = useState("");
  const [addrDialog, setAddrDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Inline Pin Lokasi modal — diaktifkan dari banner "Alamat belum punya PIN"
  // untuk update koordinat alamat existing TANPA harus pindah halaman ke Profile.
  const [pinDialogAddr, setPinDialogAddr] = useState(null); // address object atau null
  const [pinDraft, setPinDraft] = useState(""); // koordinat sementara dalam dialog
  const [pinSaving, setPinSaving] = useState(false);
  const [ratesRefreshTick, setRatesRefreshTick] = useState(0); // increment untuk paksa re-fetch rates

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/checkout"); return; }
    if (items.length === 0) return;
    api.get("/addresses").then((r) => {
      setAddresses(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) setAddressId(def.id);
    });
    // Sprint bug-fix: HAPUS fetch /shipping-methods legacy.
    // Endpoint itu mengembalikan flat static methods yang dulu dipakai sebagai
    // fallback render. Sekarang seluruh shipping data sumber dari /shipping/rates
    // (yang sudah respect shipping_mode). Tidak ada lagi double source.
    // api.get("/shipping-methods").then((r) => { setShippingMethods(r.data); if (r.data[0]) setShipping(r.data[0].code); });
  api.get("/payment-methods").then((r) => { setPaymentInfo(r.data); if (r.data.methods?.[0]) setPayment(r.data.methods[0]); else if (r.data?.midtrans?.available) setPayment("midtrans"); });
    // Service categories Jubelio (silent fail kalau Jubelio belum aktif — checkout tetap jalan via mock/flat)
    api.get("/jubelio/services/categories").then((r) => setServiceCategories(Array.isArray(r.data) ? r.data : [])).catch(() => setServiceCategories([]));
  }, [user, items.length, navigate, authLoading]);

  // Midtrans Snap.js loader — script di-load dinamis hanya kalau Midtrans available.
  // Client Key didapat dari /payment-methods. Server Key TIDAK pernah ke frontend.
  const mtAvailable = paymentInfo?.midtrans?.available;
  const mtClientKey = paymentInfo?.midtrans?.client_key;
  const mtSandbox = paymentInfo?.midtrans?.sandbox;
  useEffect(() => {
    if (!mtAvailable || !mtClientKey) return undefined;
    const snapSrc = mtSandbox
      ? "https://app.sandbox.midtrans.com/snap/snap.js"
      : "https://app.midtrans.com/snap/snap.js";
    // Hindari load script duplikat
    if (document.querySelector(`script[src="${snapSrc}"]`)) return undefined;
    const script = document.createElement("script");
    script.src = snapSrc;
    script.setAttribute("data-client-key", mtClientKey);
    script.async = true;
    document.body.appendChild(script);
    return () => {
      try { document.body.removeChild(script); } catch (_) { /* ignore */ }
    };
  }, [mtAvailable, mtClientKey, mtSandbox]);

  // Auto-fetch shipping rates dari backend setiap addressId / service_category berubah.
  // Backend yang panggil provider — API key tidak pernah keluar ke frontend.
  useEffect(() => {
    if (!addressId || items.length === 0) { setRates([]); setSelectedRate(null); return; }
    setRatesLoading(true);
    setRatesError(null);
    setRatesDegraded(false);
    setCategoryUnavailable(false);
    const body = { address_id: addressId };
    if (serviceCategoryId) body.service_category_id = serviceCategoryId;
    api.post("/shipping/rates", body)
      .then((r) => {
        const list = r.data?.rates || [];
        // ── Sprint diagnose GoSend/Grab: console debug supaya admin/dev bisa
        // verifikasi rates yang diterima frontend matches dengan backend log.
        // Aman tampil di console; tidak ada data sensitive.
        const _names = list.map((x) => x.courier_name).filter(Boolean);
        const _services = list.map((x) => x.courier_service_name || x.service).filter(Boolean);
        const _diag = r.data?.diagnostics || {};
        console.info("[shipping/rates] received", {
          count: list.length,
          courier_names: _names,
          service_names: _services,
          instant_count: _diag.instant_count,
          has_origin_coordinate: _diag.has_origin_coordinate,
          has_destination_coordinate: _diag.has_destination_coordinate,
          provider_used: r.data?.provider_used,
          degraded: r.data?.degraded,
          category_unavailable: r.data?.category_unavailable,
        });
        setRates(list);
        setRatesDegraded(!!r.data?.degraded);
        setCategoryUnavailable(!!r.data?.category_unavailable);
        setProviderUsed(r.data?.provider_used || null);
        setShippingMode((r.data?.shipping_mode || "realtime").toLowerCase());
        // Sprint bug-fix: log mode + render decision di console supaya admin bisa
        // verifikasi tidak ada double render saat mode realtime aktif.
        console.info("[checkout] shipping_mode =", r.data?.shipping_mode || "realtime",
          "rates =", list.length, "flat render = false (legacy fallback removed)");
        // Hanya tampilkan technical error ke customer kalau benar-benar TIDAK ada rate
        // (fallback gagal semua). Kalau ada rate valid → log diam ke console saja.
        if (list.length === 0 && r.data?.error_reason) {
          setRatesError(r.data.error_reason);
        } else if (r.data?.error_reason) {
          // Internal info log untuk dev/admin, customer TIDAK lihat
          console.info("[shipping/rates] fallback aktif:", r.data.error_reason);
        }
        if (list[0]) setSelectedRate(list[0]);
        else setSelectedRate(null);
      })
      .catch((e) => {
        setRatesError(formatApiError(e));
        setRates([]);
      })
      .finally(() => setRatesLoading(false));
  }, [addressId, items.length, serviceCategoryId, ratesRefreshTick]);

  const isBiteship = providerUsed === "biteship";
  const biteshipTypes = useMemo(() => (isBiteship ? biteshipServiceTypes(rates) : []), [isBiteship, rates]);
  const visibleRates = useMemo(() => (isBiteship ? filterRatesByServiceType(rates, serviceTypeFilter) : rates), [isBiteship, rates, serviceTypeFilter]);
  useEffect(() => { setServiceTypeFilter((f) => reconcileServiceTypeFilter(rates, f)); }, [rates]);
  useEffect(() => {
    if (!isBiteship || !serviceTypeFilter) return;
    const stillVisible = selectedRate && visibleRates.some((r) => r.courier_code === selectedRate.courier_code && r.service === selectedRate.service);
    if (!stillVisible) setSelectedRate(visibleRates[0] || null);
  }, [isBiteship, serviceTypeFilter, visibleRates, selectedRate]);

  const reloadAddresses = async () => {
    const r = await api.get("/addresses");
    setAddresses(r.data);
    if (!addressId && r.data[0]) setAddressId(r.data[0].id);
    setAddrDialog(false);
  };

  // Buka dialog pilih PIN Lokasi untuk address yang sedang dipilih.
  const openPinDialog = (addr) => {
    if (!addr) return;
    setPinDraft(addr.coordinate || "");
    setPinDialogAddr(addr);
  };

  // Simpan koordinat ke alamat existing via PUT /addresses/{id}.
  // Penting: pakai PUT, BUKAN PATCH — backend AddressIn schema strict (wajib
  // recipient_name, phone, address, city). Jadi kita kirim ulang full payload
  // dari address object, lalu override coordinate. Field "_id"/internal harus
  // dibuang supaya pydantic validation tidak gagal.
  const savePinForAddress = async () => {
    if (!pinDialogAddr) return;
    if (!pinDraft) {
      toast.error("Pilih titik di peta dulu");
      return;
    }
    setPinSaving(true);
    try {
      const a = pinDialogAddr;
      const payload = {
        label: a.label || "Rumah",
        recipient_name: a.recipient_name,
        phone: a.phone,
        address: a.address,
        province: a.province ?? null,
        city: a.city || "",
        district: a.district ?? null,
        subdistrict: a.subdistrict ?? null,
        postal_code: a.postal_code || "",
        is_default: !!a.is_default,
        area_id: a.area_id ?? null,
        province_id: a.province_id ?? null,
        city_id: a.city_id ?? null,
        district_id: a.district_id ?? null,
        coordinate: pinDraft,
      };
      await api.put(`/addresses/${a.id}`, payload);
      toast.success("Pin lokasi tersimpan");
      // Refresh address list & paksa rates re-fetch supaya kurir instant muncul
      const r = await api.get("/addresses");
      setAddresses(r.data);
      setPinDialogAddr(null);
      setRatesRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPinSaving(false);
    }
  };

  const applyVoucher = async () => {
    if (!voucherCode) return;
    try {
      const { data } = await api.post("/vouchers/validate", null, { params: { code: voucherCode, subtotal: cartTotal } });
      setVoucher(data);
      toast.success("Voucher diterapkan");
    } catch (e) { setVoucher(null); toast.error(formatApiError(e)); }
  };

  // Picker handler — voucher object dari /vouchers/available.
  // useCallback supaya referensi stabil & VoucherPicker tidak re-fetch karena
  // perubahan referensi onPick (lihat dep array di VoucherPicker.useEffect).
  const pickVoucher = useCallback((v) => {
    if (!v) {
      setVoucher(null);
      setVoucherCode("");
      return;
    }
    setVoucherCode(v.code || "");
    setVoucher({
      voucher: v,
      discount: v.discount_preview || 0,
      free_shipping: !!v.free_shipping_preview,
    });
  }, []);

  const selectedShipping = shippingMethods.find((s) => s.code === shipping);
  // Prioritas: rate dari /shipping/rates → fallback ke shipping-methods lama
  let shippingCost = selectedRate?.cost ?? selectedShipping?.base_cost ?? 0;
  if (voucher?.free_shipping) shippingCost = 0;
  const discount = voucher?.discount || 0;

  // ── Biteship COD (Phase 2): opsi tampil hanya bila tenant ON + rate Biteship eligible; fee/total dari /shipping/cod-quote.
  const codVisible = codOptionVisible({ paymentInfo, providerUsed, selectedRate });
  const appliedVoucherCode = voucher ? voucherCode : "";
  const codKey = codQuoteKey({ selectedRate, addressId, items, voucherCode: appliedVoucherCode });
  const firstPrepaid = paymentInfo?.methods?.[0] || (paymentInfo?.midtrans?.available ? "midtrans" : "");
  useEffect(() => {
    setCodQuote(null); setCodError(null); // dependensi berubah → quote lama stale
    if (payment !== COD_METHOD) return undefined;
    if (!codVisible) { setPayment(firstPrepaid); return undefined; } // fail-closed: kembali ke prepaid
    let alive = true;
    setCodLoading(true);
    api.post("/shipping/cod-quote", { address_id: addressId, rate_quote_id: selectedRate.rate_quote_id, voucher_code: appliedVoucherCode || null })
      .then((r) => { if (alive) setCodQuote(r.data); })
      .catch((e) => {
        if (!alive) return;
        const msg = formatApiError(e);
        setCodError(msg); setPayment(firstPrepaid);
        toast.error(`COD tidak tersedia: ${msg}`);
      })
      .finally(() => { if (alive) setCodLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, codKey, codVisible]);
  const summary = checkoutSummary({ cartTotal, shippingCost, discount, payment, codQuote });
  const total = summary.total;

  const placeOrder = async () => {
    if (!addressId || !payment) {
      toast.error("Lengkapi alamat dan pembayaran");
      return;
    }
    // Jika /shipping/rates sudah memberi opsi, customer WAJIB pilih dari sana.
    // Jangan lagi fallback ke radio shipping_method legacy karena bisa scrambled data.
    if (rates.length > 0 && !selectedRate) {
      toast.error("Pilih kurir terlebih dahulu");
      return;
    }
    if (rates.length === 0 && !shipping) {
      toast.error("Pilih kurir terlebih dahulu");
      return;
    }
    if (payment === COD_METHOD && (!codQuote || summary.mismatch || codQuote.rate_quote_id !== selectedRate?.rate_quote_id)) {
      toast.error("Quote COD belum siap atau sudah berubah. Pilih ulang metode COD.");
      return;
    }
    setLoading(true);
    try {
      // Saat ada selectedRate, SHIPPING_METHOD legacy diabaikan — pakai data rate.
      // Backend tetap perlu shipping_method untuk validasi enum, jadi kirim string
      // turunan dari rate (lowercase courier_code) supaya tidak nyangkut di SHIPPING_METHODS.
      const isStoreDelivery = selectedRate?.courier_code === "STORE_DELIVERY";
      const fallbackShipMethod = selectedRate
        ? (isStoreDelivery ? "STORE_DELIVERY" : (selectedRate.courier_code || selectedRate.courier_name || "JBL").toLowerCase())
        : shipping;
      const body = {
        address_id: addressId,
        shipping_method: fallbackShipMethod,
        payment_method: payment,
        voucher_code: voucher ? voucherCode : null,
        notes: notes || null,
      };
      if (selectedRate) {
        // PASS LENGKAP — backend akan validasi konsistensi (Jubelio butuh courier_id)
        body.courier_code = selectedRate.courier_code;
        body.courier_name = selectedRate.courier_name;
        body.courier_service = selectedRate.service;
        body.courier_service_category = selectedRate.courier_service_category || null;
        body.shipping_cost_override = voucher?.free_shipping ? 0 : selectedRate.cost;
        // Marker provider untuk gate Generate AWB
        body.provider_used = selectedRate.provider || providerUsed || null;
        // Biteship: harga server-authoritative via quote id (backend mengabaikan override)
        if (selectedRate.rate_quote_id) body.rate_quote_id = selectedRate.rate_quote_id;
        if (payment === COD_METHOD) body.cod_quote_id = codQuote.cod_quote_id; // fee/total divalidasi ulang server
        body.degraded = !!ratesDegraded;
        // Jubelio v1.8 numeric IDs (WAJIB kalau provider="jubelio")
        if (selectedRate.courier_id != null) body.courier_id = Number(selectedRate.courier_id);
        if (selectedRate.courier_service_id != null) body.courier_service_id = Number(selectedRate.courier_service_id);
      }
      const { data } = await api.post("/checkout", body);
      await fetchCart();

      // Midtrans flow: setelah order dibuat, request Snap Token dan buka popup.
      // Status pesanan TETAP pending_payment sampai Phase 2 webhook konfirmasi (sesuai constraint Phase 1).
      if (payment === "midtrans") {
        try {
          const snapResp = await api.post(`/orders/${data.id}/payment/midtrans/snap`, {});
          const token = snapResp?.data?.snap_token;
          if (!token) throw new Error("Token Snap kosong");
          if (typeof window === "undefined" || !window.snap) {
            toast.error("Snap.js belum siap. Coba refresh halaman dan ulangi.");
            navigate(`/pesanan/${data.id}`);
            return;
          }
          toast.success("Pesanan dibuat. Lanjutkan pembayaran via popup.");
          window.snap.pay(token, {
            onSuccess: (res) => {
              // Phase 1: tidak update payment_status di sini — webhook Phase 2 yang otoritatif.
              toast.success("Pembayaran berhasil! Status akan diperbarui sebentar lagi.");
              navigate(`/pesanan/${data.id}`);
              console.info("[midtrans] onSuccess", res);
            },
            onPending: (res) => {
              toast.info("Menunggu pembayaran Anda. Selesaikan sesuai instruksi.");
              navigate(`/pesanan/${data.id}`);
              console.info("[midtrans] onPending", res);
            },
            onError: (res) => {
              toast.error("Pembayaran gagal. Silakan coba lagi atau gunakan metode lain.");
              navigate(`/pesanan/${data.id}`);
              console.warn("[midtrans] onError", res);
            },
            onClose: () => {
              toast.warning("Popup pembayaran ditutup. Anda bisa melanjutkan dari halaman pesanan.");
              navigate(`/pesanan/${data.id}`);
            },
          });
          return;
        } catch (snapErr) {
          toast.error(`Gagal memulai pembayaran online: ${formatApiError(snapErr)}`);
          navigate(`/pesanan/${data.id}`);
          return;
        }
      }

      toast.success("Pesanan dibuat!");
      navigate(`/pesanan/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const labelMap = { bca: "Transfer BCA", mandiri: "Transfer Mandiri", bni: "Transfer BNI", bri: "Transfer BRI", qris: "QRIS" };

  if (items.length === 0) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center">
        <h2 className="font-display text-2xl font-bold">Keranjang Kosong</h2>
        <Link to="/katalog"><Button className="mt-5 system-btn-primary rounded-xl">Mulai Belanja</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <PromoPopup location="checkout" />
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-6">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {/* Address */}
          <section className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin className="text-[#111827]" size={18} />
                <h3 className="font-display font-bold">Alamat Pengiriman</h3>
              </div>
              <Dialog open={addrDialog} onOpenChange={setAddrDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-xl" data-testid="add-address-btn"><Plus size={14} /> Tambah</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Tambah Alamat</DialogTitle></DialogHeader>
                  <AddressForm onSaved={reloadAddresses} />
                </DialogContent>
              </Dialog>
            </div>
            {addresses.length === 0 ? (
              <div className="text-sm text-[color:var(--color-text-secondary)]">Belum ada alamat. Tambahkan terlebih dahulu.</div>
            ) : (
              <RadioGroup value={addressId} onValueChange={setAddressId} className="space-y-2">
                {addresses.map((a) => (
                  <Label key={a.id} htmlFor={a.id} className={`flex gap-3 p-3 rounded-xl border cursor-pointer ${addressId === a.id ? "border-[#111827] system-soft-bg" : "border-[color:var(--color-border)]"}`}>
                    <RadioGroupItem id={a.id} value={a.id} data-testid={`addr-radio-${a.id}`} />
                    <div className="text-sm flex-1">
                      <div className="font-bold flex flex-wrap items-center gap-1.5">
                        <span>{a.label} · {a.recipient_name}</span>
                        {a.coordinate && (
                          <span className="text-[9px] bg-[#2D6A4F]/10 text-[#2D6A4F] font-bold px-1.5 py-0.5 rounded flex items-center gap-1" data-testid={`checkout-addr-pin-ok-${a.id}`}>
                            <MapPin size={9} /> PIN AKTIF
                          </span>
                        )}
                      </div>
                      <div className="text-[color:var(--color-text-secondary)]">{a.phone}</div>
                      <div className="text-[color:var(--color-text-secondary)]">{a.address}, {a.city} {a.postal_code}</div>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            )}
          </section>

          {/* Shipping */}
          <section className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-2xl p-5" data-testid="shipping-section">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="text-[#111827]" size={18} />
              <h3 className="font-display font-bold">Metode Pengiriman</h3>
              {ratesLoading && <span className="text-xs text-[color:var(--color-text-secondary)]">Memuat tarif...</span>}
              {ratesDegraded && <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded" data-testid="rates-degraded-badge">TARIF ESTIMASI</span>}
            </div>

            {/* Banner: alamat belum punya koordinat → kurir instant tidak tersedia */}
            {addressId && !addresses.find((x) => x.id === addressId)?.coordinate && (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="checkout-no-coordinate-banner">
                <div className="font-bold flex items-center gap-1">
                  <MapPin size={12} /> Pin lokasi belum ditambahkan
                </div>
                <div className="mt-1">
                  Tambahkan pin lokasi agar kurir instant tersedia dan alamat
                  lebih mudah ditemukan.
                </div>
                <button
                  type="button"
                  onClick={() => openPinDialog(addresses.find((x) => x.id === addressId))}
                  className="inline-block mt-2 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 transition"
                  data-testid="checkout-add-pin-btn"
                >
                  Pilih Pin Lokasi
                </button>
              </div>
            )}
            {addressId && addresses.find((x) => x.id === addressId)?.coordinate && (
              <div className="mb-3 bg-[#2D6A4F]/5 border border-[#2D6A4F]/20 rounded-lg p-3 text-xs text-[#2D6A4F] flex items-start gap-1.5" data-testid="checkout-pin-ok-banner">
                <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                <span>Pin lokasi sudah tersimpan.</span>
              </div>
            )}

            {isBiteship && biteshipTypes.length > 0 && (
              <div className="mb-3" data-testid="service-type-section">
                <label className="text-xs font-bold text-[color:var(--color-text-secondary)] uppercase tracking-wider">Tipe Layanan</label>
                <select
                  value={serviceTypeFilter}
                  disabled={ratesLoading}
                  onChange={(e) => setServiceTypeFilter(e.target.value)}
                  className="w-full mt-1 border border-[color:var(--color-border)] rounded-lg p-2 text-sm bg-white"
                  data-testid="service-type-select"
                >
                  <option value="">Semua tipe ({rates.length} layanan)</option>
                  {biteshipTypes.map((t) => (
                    <option key={t.code} value={t.code}>{t.label} ({filterRatesByServiceType(rates, t.code).length})</option>
                  ))}
                </select>
              </div>
            )}
            {!isBiteship && serviceCategories.length > 0 && (
              <div className="mb-3" data-testid="service-category-section">
                <label className="text-xs font-bold text-[color:var(--color-text-secondary)] uppercase tracking-wider">Tipe Layanan</label>
                <select
                  value={serviceCategoryId}
                  onChange={(e) => setServiceCategoryId(e.target.value)}
                  className="w-full mt-1 border border-[color:var(--color-border)] rounded-lg p-2 text-sm bg-white"
                  data-testid="service-category-select"
                >
                  <option value="">Semua tipe</option>
                  {serviceCategories.map((c) => (
                    <option key={c.service_category_id} value={c.service_category_id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            {ratesError && rates.length === 0 && (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="rates-error">
                <div className="font-bold">Ongkos kirim sedang tidak dapat dihitung.</div>
                <div className="mt-1">Silakan coba beberapa saat lagi atau hubungi admin.</div>
              </div>
            )}
            {categoryUnavailable && rates.length > 0 && (
              <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex gap-2" data-testid="category-unavailable-info">
                <span aria-hidden>ℹ</span>
                <span>Kategori layanan yang Anda pilih tidak tersedia untuk rute ini. Berikut adalah opsi kurir lain yang tersedia.</span>
              </div>
            )}

            {rates.length > 0 ? (
              <CourierRatesGrouped
                rates={visibleRates}
                selectedRate={selectedRate}
                onSelect={setSelectedRate}
              />
            ) : (
              // Sprint bug-fix: TIDAK ADA fallback ke flat statis (shippingMethods).
              // Saat rates kosong → tampilkan empty state. Backend yang menentukan
              // apa yang dikirim sesuai shipping_mode (realtime=Jubelio, flat=manual).
              !ratesLoading && !ratesError && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="rates-empty">
                  Belum ada layanan kurir yang tersedia untuk alamat ini.
                  Pastikan alamat lengkap & pin lokasi aktif, atau hubungi admin.
                </div>
              )
            )}
          </section>

          {/* Payment */}
          <section className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-2xl p-5" data-testid="payment-section">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="text-[#111827]" size={18} />
              <h3 className="font-display font-bold">Metode Pembayaran</h3>
            </div>
            <RadioGroup value={payment} onValueChange={setPayment} className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {paymentInfo.methods.map((m) => (
                <Label key={m} className={`p-3 rounded-xl border cursor-pointer text-sm font-bold ${payment === m ? "border-[#111827] system-soft-bg" : "border-[color:var(--color-border)]"}`}>
                  <RadioGroupItem value={m} className="mr-2" data-testid={`pay-${m}`} /> {labelMap[m]}
                </Label>
              ))}
              {codVisible && (
                <Label
                  className={`p-3 rounded-xl border cursor-pointer text-sm font-bold col-span-2 sm:col-span-3 ${payment === COD_METHOD ? "border-[#111827] system-soft-bg" : "border-[color:var(--color-border)]"}`}
                  data-testid="pay-biteship-cod-label"
                >
                  <RadioGroupItem value={COD_METHOD} className="mr-2" data-testid="pay-biteship-cod" />
                  Bayar di Tempat (COD)
                  <div className="text-[10px] font-normal text-[color:var(--color-text-secondary)] mt-1">
                    Bayar tunai ke kurir saat paket diterima. Biaya COD ditentukan oleh Biteship untuk layanan kurir yang dipilih.
                  </div>
                </Label>
              )}
              {paymentInfo?.midtrans?.available && (
                <Label
                  className={`p-3 rounded-xl border cursor-pointer text-sm font-bold col-span-2 sm:col-span-3 ${payment === "midtrans" ? "border-[#111827] system-soft-bg" : "border-[color:var(--color-border)]"}`}
                  data-testid="pay-midtrans-label"
                >
                  <RadioGroupItem value="midtrans" className="mr-2" data-testid="pay-midtrans" />
                  Bayar Online (Midtrans){paymentInfo.midtrans.sandbox ? " · Sandbox" : ""}
                  <div className="text-[10px] font-normal text-[color:var(--color-text-secondary)] mt-1">
                    Kartu kredit, GoPay, OVO, ShopeePay, VA bank, dan lainnya. Popup akan terbuka setelah klik &quot;Buat Pesanan&quot;.
                  </div>
                </Label>
              )}
            </RadioGroup>

            {payment === "qris" && paymentInfo.qris_image && (
              <div className="mt-4 p-4 rounded-2xl system-soft-bg border border-[color:var(--color-border)]" data-testid="qris-checkout-preview">
                <div className="text-xs uppercase tracking-widest font-bold mb-2">Preview QRIS</div>
                <div className="flex items-center gap-3">
                  <img src={imageUrl(paymentInfo.qris_image)} alt="QRIS" className="w-32 h-32 rounded-lg bg-white p-2 border border-[color:var(--color-border)] object-contain" />
                  <div className="text-xs text-[color:var(--color-text-secondary)] flex-1">
                    Pembayaran via QRIS akan tampil saat order dibuat. Scan QRIS sesuai total pembayaran, lalu upload bukti.
                  </div>
                </div>
              </div>
            )}
            {payment === "qris" && !paymentInfo.qris_image && (
              <div className="mt-3 text-xs text-[color:var(--danger)] bg-red-50 rounded-xl p-3">
                QRIS belum tersedia. Hubungi admin atau pilih metode pembayaran lain.
              </div>
            )}
            {payment === "midtrans" && (
              <div className="mt-3 text-xs text-[color:var(--color-text-secondary)] bg-[color:var(--color-surface-secondary)] rounded-xl p-3" data-testid="midtrans-hint">
                Popup pembayaran Midtrans akan otomatis terbuka. Jangan tutup tab sampai pembayaran selesai.
              </div>
            )}
            {payment === COD_METHOD && (
              <div className="mt-3 text-xs rounded-xl p-3 bg-[color:var(--color-surface-secondary)] text-[color:var(--color-text-secondary)] space-y-1" data-testid="cod-detail">
                {codLoading && <div data-testid="cod-loading">Menghitung biaya COD dari Biteship…</div>}
                {!codLoading && codError && <div className="text-[color:var(--danger)]" data-testid="cod-error">{codError}</div>}
                {!codLoading && codQuote && (
                  <>
                    <div className="flex justify-between"><span>Layanan</span><span className="font-semibold" data-testid="cod-service">{codQuote.courier_name} · {codQuote.courier_service_name || codQuote.service}</span></div>
                    <div className="flex justify-between"><span>Ongkir</span><span data-testid="cod-shipping">{formatRp(codQuote.shipping_cost)}</span></div>
                    <div className="flex justify-between"><span>Biaya COD (Biteship)</span><span data-testid="cod-fee">{formatRp(codQuote.cod_fee)}</span></div>
                    <div className="flex justify-between font-bold text-[color:var(--color-text)]"><span>Dibayar saat barang diterima</span><span data-testid="cod-total">{formatRp(codQuote.cod_total)}</span></div>
                  </>
                )}
              </div>
            )}
            {payment !== "qris" && payment !== "midtrans" && payment !== COD_METHOD && (
              <div className="text-xs text-[color:var(--color-text-secondary)] mt-3">Setelah pesanan dibuat, anda akan diminta upload bukti transfer.</div>
            )}
          </section>

          {/* Notes */}
          <section className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-2xl p-5">
            <Label className="text-sm font-bold">Catatan untuk Penjual (opsional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Contoh: dikirim sore hari" className="mt-2" data-testid="notes-input" />
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-2xl p-5 h-fit sticky top-24">
          <h3 className="font-display font-bold mb-3">Ringkasan Pesanan</h3>
          <div className="space-y-2 max-h-48 overflow-auto mb-3">
            {items.map((i) => (
              <div key={i.id} className="flex gap-2 text-sm">
                <img src={imageUrl(i.product.images?.[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="line-clamp-1 font-semibold text-xs">{i.product.name}</div>
                  <div className="text-xs text-[color:var(--color-text-secondary)]">{i.quantity}x · {formatRp(i.product.sale_price || i.product.price)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[color:var(--color-border)] pt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[color:var(--color-text-secondary)]">Subtotal</span><span>{formatRp(cartTotal)}</span></div>
            <div className="flex justify-between"><span className="text-[color:var(--color-text-secondary)]">Ongkir</span><span>{formatRp(shippingCost)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#2D6A4F]"><span>Diskon Voucher</span><span>-{formatRp(discount)}</span></div>}
            {summary.isCod && <div className="flex justify-between" data-testid="summary-cod-fee"><span className="text-[color:var(--color-text-secondary)]">Biaya COD (Biteship)</span><span>{formatRp(summary.codFee)}</span></div>}
          </div>
          <div className="mt-3 pt-3 border-t border-[color:var(--color-border)] flex justify-between items-baseline">
            <span className="font-semibold">{summary.isCod ? "Total dibayar saat diterima" : "Total"}</span>
            <span className="font-display font-black text-2xl commerce-price" data-testid="checkout-total">{formatRp(total)}</span>
          </div>

          <div className="mt-4 space-y-3" data-testid="voucher-section">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-[color:var(--color-text-secondary)] uppercase tracking-wider">Voucher</div>
              {voucher && (
                <button onClick={() => { setVoucher(null); setVoucherCode(""); }} className="text-[11px] system-link font-semibold" data-testid="voucher-clear-btn">Lepas voucher</button>
              )}
            </div>
            <VoucherPicker
              subtotal={cartTotal}
              selected={voucher?.voucher || null}
              onPick={pickVoucher}
            />
            <details className="text-xs">
              <summary className="cursor-pointer text-[color:var(--color-text-secondary)] hover:underline">Punya kode voucher khusus? Masukkan manual</summary>
              <div className="flex gap-2 mt-2">
                <Input placeholder="Kode voucher" value={voucherCode} onChange={(e) => setVoucherCode(e.target.value.toUpperCase())} className="rounded-xl" data-testid="voucher-input" />
                <Button onClick={applyVoucher} variant="outline" className="rounded-xl" data-testid="voucher-apply-btn"><Tag size={14} /></Button>
              </div>
            </details>
            {voucher && (
              <div className="text-xs text-[#2D6A4F] flex items-center gap-1" data-testid="voucher-active-label">
                <CheckCircle2 size={12} /> Voucher <b>{voucherCode}</b> aktif
                {voucher.free_shipping ? " · Gratis Ongkir" : voucher.discount > 0 ? ` · Hemat ${formatRp(voucher.discount)}` : ""}
              </div>
            )}
          </div>

          <Button
            onClick={placeOrder}
            disabled={loading || !addressId || !(selectedRate || shipping) || !payment || (payment === COD_METHOD && (codLoading || !codQuote || summary.mismatch))}
            className="mt-4 w-full h-12 color-btn-primary rounded-xl font-bold"
            data-testid="place-order-btn"
          >
            {loading ? "Memproses..." : "Buat Pesanan"}
          </Button>
        </aside>
      </div>

      {/* Inline PIN Lokasi Dialog — buka dari warning banner di section Shipping.
          Simpan langsung ke alamat existing (PUT /addresses/{id}) dan refresh
          shipping rates supaya kurir instant muncul tanpa keluar dari Checkout. */}
      {pinDialogAddr && (
        <Dialog open={!!pinDialogAddr} onOpenChange={(o) => { if (!o) setPinDialogAddr(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Tambah / Ubah Pin Lokasi</DialogTitle>
              <DialogDescription>
                Untuk alamat: <b>{pinDialogAddr.label}</b> · {pinDialogAddr.recipient_name}
              </DialogDescription>
            </DialogHeader>
            <LocationPicker
              value={pinDraft}
              onChange={(v) => setPinDraft(v)}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPinDialogAddr(null)}
                className="rounded-xl"
                data-testid="checkout-pin-cancel"
              >
                Batal
              </Button>
              <Button
                type="button"
                onClick={savePinForAddress}
                disabled={pinSaving}
                className="system-btn-primary rounded-xl"
                data-testid="checkout-pin-save"
              >
                {pinSaving ? "Menyimpan..." : "Simpan Pin"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
